import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import ReadOnlyField from './readonly-field';
import { Dealer } from '../../types';

interface DealerFieldProps<TFieldValues extends FieldValues> {
  /**
   * Field name for the dealer ID in the form
   */
  readonly name: Path<TFieldValues>;

  /**
   * Form control from react-hook-form
   */
  readonly control: Control<TFieldValues>;

  /**
   * List of available dealers
   */
  readonly dealers: Dealer[];

  /**
   * Whether the field is in read-only mode
   */
  readonly isReadOnly: boolean;

  /**
   * Label for the field (defaults to "Dealer")
   */
  readonly label?: string;

  /**
   * Whether the field is required (defaults to true)
   */
  readonly required?: boolean;

  /**
   * Function to get the dealer name for display in read-only mode
   */
  readonly getDealerName?: () => string;

  /**
   * Whether the dealers are currently loading
   */
  readonly isLoading?: boolean;

  /**
   * Whether the field is disabled (in edit mode)
   */
  readonly disabled?: boolean;

  /**
   * Whether a manufacturer must be selected first
   */
  readonly requiresManufacturer?: boolean;

  /**
   * Whether the manufacturer has been selected
   */
  readonly hasManufacturer?: boolean;

  /**
   * Optional callback for when dealer is changed by user
   */
  readonly onDealerChange?: (dealerId: string) => void;
}

/**
 * A reusable dealer field component that can be displayed in both
 * edit mode (dropdown selector) and read-only mode (text display).
 */
const DealerField = <TFieldValues extends FieldValues>({
  name,
  control,
  dealers,
  isReadOnly,
  label = 'Dealer',
  required = true,
  getDealerName = () => '',
  isLoading = false,
  disabled = false,
  requiresManufacturer = true,
  hasManufacturer = false,
  onDealerChange = () => {},
}: DealerFieldProps<TFieldValues>) => {
  const fieldLabel = isLoading ? `Loading ${label}s...` : label;
  const lowerCaseLabel = label.charAt(0).toLowerCase() + label.slice(1);

  return (
    <Grid item xs={12}>
      {isReadOnly ? (
        <ReadOnlyField label={label} value={getDealerName()} />
      ) : (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState }) => (
            <FormControl fullWidth error={!!fieldState.error} required={required}>
              <InputLabel id={`${String(name)}-select-label`}>{fieldLabel}</InputLabel>
              <Select
                {...field}
                labelId={`${String(name)}-select-label`}
                label={fieldLabel}
                value={field.value || ''}
                disabled={disabled || isLoading || (requiresManufacturer && !hasManufacturer)}
                onChange={e => {
                  field.onChange(e);
                  if (onDealerChange) {
                    onDealerChange(e.target.value);
                  }
                }}
              >
                {dealers.map(dealer => (
                  <MenuItem key={dealer.id} value={dealer.id}>
                    {dealer.name}
                    {!dealer.status ? ' (Inactive)' : ''}
                  </MenuItem>
                ))}
              </Select>
              {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              {requiresManufacturer && !hasManufacturer && (
                <FormHelperText>Select a manufacturer first</FormHelperText>
              )}
              {requiresManufacturer && hasManufacturer && dealers.length === 0 && !isLoading && (
                <FormHelperText>No {lowerCaseLabel}s found for this manufacturer</FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}
    </Grid>
  );
};

export default DealerField;
